import { Injectable } from '@angular/core';
import { Sample, PpeAssignmentPayload } from '../../../../models/sample.model';
import { IhLabResult } from '../../../../models/IhLabResult.model';
import { PpeAssignment } from '../../../../models/ppeAssignment.model';
import { PpeTemplateData } from '../../../../models/ppeTemplateData.model';
import { Units, UnitSymbols } from '../../../../enums/units.enum';
import { SampleCategoryEnum } from '../../../../enums/sampleCategory.enum';
import { SampleCensorType } from '../../../../enums/sampleCensorType.enum';
import { DateFormatService } from '../../../../services/dateFormat.service';
import { ValueConversionService } from '../../../../services/value-conversion.service';
import { MultiSampleData, OelTypeData, ProcessedSample } from './multi-sample-table-widget.component';
import { sanitizeCssColor } from '../../../../shared/utils/css-color.util';
import { chunkArray } from '../../../../shared/utils/array.util';
import { ResolvedExposureBand, EMPTY_EXPOSURE_BAND } from '../../../../models/risk-assessment.model';

export interface MultiSampleTablePresentationLabels {
    concentrationsHeader: string;
    sampleNumber: string;
    jobTitleArea: string;
    oelHeaderLine1: string;
    oelHeaderLine2: string;
    oelHeaderLine3: string;
    sampleType: string;
    respType: string;
    respApf: string;
    mucExceeded: string;
    startDate: string;
    startTime: string;
    durationMin: string;
    volumeLiters: string;
    taskArea: string;
    noTaskDescription: string;
}

export interface MultiSampleTablePresentationOptions {
    selectedOelFilters: (number | string)[];
    excludeAdjustedOel: boolean;
    excludedOelColumnKeys?: string[];
}

export interface MultiSampleSampleColumnView {
    sampleNumber: string | number;
    segName: string;
    sampleType: string;
    respType: string;
    respApf: string;
    mucExceeded: string;
    startDate: string;
    startTime: string;
    duration: string;
    volume: string;
}

export interface MultiSampleOelColumnView {
    oelTypeName: string;
    headerStyle: string;
}

export interface MultiSampleFieldRowView {
    label: string;
    values: string[];
}

export interface MultiSampleCellView {
    display: string;
    cellStyle: string;
}

export interface MultiSampleAgentRowView {
    agentName: string;
    sampleCells: MultiSampleCellView[];
    oelCells: MultiSampleCellView[];
}

export interface MultiSampleDisplayTableView {
    showJurisdictionHeader: boolean;
    jurisdictionName: string;
    agentRows: MultiSampleAgentRowView[];
}

export interface MultiSampleLegendItemView {
    legendText: string;
    swatchStyle: string;
}

export interface MultiSampleConcentrationCellView {
    concentration: string;
    cellStyle: string;
}

export interface MultiSampleOelValueCellView {
    oelValue: string;
    cellStyle: string;
}

export interface MultiSampleAgentTemplateRowView {
    agentName: string;
    concentrationCells: MultiSampleConcentrationCellView[];
    oelValueCells: MultiSampleOelValueCellView[];
}

export interface MultiSampleTableGroupView {
    sampleColumns: MultiSampleSampleColumnView[];
    oelColumns: MultiSampleOelColumnView[];
    oelColumnCount: number;
    hasOelColumns: boolean;
    jurisdictionColspan: number;
    fieldRows: MultiSampleFieldRowView[];
    displayTables: MultiSampleDisplayTableView[];
    legendItems: MultiSampleLegendItemView[];
    hasLegend: boolean;
    isFirstGroup: boolean;
    concentrationsHeaderLabel: string;
    sampleNumberLabel: string;
    jobTitleAreaLabel: string;
    oelHeaderLabelLine1: string;
    oelHeaderLabelLine2: string;
    oelHeaderLabelLine3: string;
    agentRows: MultiSampleAgentTemplateRowView[];
    showJurisdictionHeader: boolean;
    jurisdictionName: string;
    totalColspan: number;
    oelRowspan: number;
}

export interface MultiSampleTaskAreaRowView {
    sampleNumber: string | number;
    taskDescription: string;
}

export interface MultiSampleTablePresentation {
    tableGroups: MultiSampleTableGroupView[];
    taskAreaRows: MultiSampleTaskAreaRowView[];
    hasData: boolean;
    concentrationsHeaderLabel: string;
    sampleNumberLabel: string;
    jobTitleAreaLabel: string;
    taskAreaLabel: string;
}

/** One flat, pre-formatted row per (sample x ihLabResult) for the MultiSample detailed-results table. */
export interface MultiSampleDetailedResultRow {
    date: string;
    sampleNumber: string | number;
    agentName: string;
    jurisdictionName: string;
    durationMinutes: number;
    result: string;
    exposureLimit: string;
    percentOfOel: string;
    percentOfOelValue: number | null;
    exposureBandName: string;
    exposureBandColor: string;
    exposureBand: ResolvedExposureBand;
    locationComments: string;
    segName: string;
    segRating: string;
    segRatingColor: string;
    methodOfAssessment: string;
}

/** SEG risk rating + method of assessment for one (SEG, agent), resolved from the current RA. */
export interface DetailedResultSegRating {
    rating: string;
    ratingColor: string;
    methodOfAssessment: string;
}

/**
 * Values the row builder cannot derive from the sample/lab-result alone. The mapper supplies these
 * bound to the loaded exposure-likelihood levels (band) and the SEG profile summary (RA rating), so
 * this presentation service stays free of the risk-config / SEG-profile services.
 */
export interface DetailedResultRowResolvers {
    resolveExposureBand: (percentOfOel: number, jurisdictionId: string | undefined) => ResolvedExposureBand;
    resolveSegRating: (segId: string | undefined, agentId: string) => DetailedResultSegRating;
}

interface InternalAgentRow {
    agentId: string;
    agentName: string;
    agentUnits: Units;
    oelTypes: OelTypeData[];
    oelValueByColumnKey: Record<string, string>;
    sampleCells: MultiSampleCellView[];
}

interface InternalJurisdictionTable {
    jurisdictionId: string;
    jurisdictionName: string;
    agentRows: InternalAgentRow[];
}

interface InternalTableGroup {
    samples: ProcessedSample[];
    agentRows: InternalAgentRow[];
    jurisdictionTables: InternalJurisdictionTable[];
    oelColumns: OelTypeData[];
    oelColumnCount: number;
}

type PpeItem = PpeAssignment | PpeTemplateData | PpeAssignmentPayload;
type PpeBase = { name?: string; ppeName?: string; apf?: string; ppeType?: number; iconUrl?: string; rating?: number };

const SAMPLES_PER_TABLE = 3;
const AGENTS_PER_TABLE = 14;
const OEL_COLORS = ['#81c784', '#64b5f6', '#fff176', '#ffb74d', '#ba68c8', '#4db6ac', '#e57373', '#7986cb'];
const CENSOR_SYMBOLS: Record<number, string> = { 0: '>', 1: '<', 2: '', 3: '' };
const LOQ_CELL_STYLE = 'background-color:#9E9E9E !important;color:#ffffff';
const FALLBACK_OEL_COLOR = '#81c784';

@Injectable({ providedIn: 'root' })
export class MultiSampleTablePresentationService {
    constructor(
        private dateFormatService: DateFormatService,
        private valueConversionService: ValueConversionService
    ) { }

    build(
        data: MultiSampleData | null | undefined,
        options: MultiSampleTablePresentationOptions,
        labels: MultiSampleTablePresentationLabels
    ): MultiSampleTablePresentation {
        const validSamples = data?.samples?.filter(s => s.sampleCategory !== SampleCategoryEnum.Blank && (s.ihLabResults?.length ?? 0) > 0);
        const empty: MultiSampleTablePresentation = {
            tableGroups: [],
            taskAreaRows: [],
            hasData: false,
            concentrationsHeaderLabel: labels.concentrationsHeader,
            sampleNumberLabel: labels.sampleNumber,
            jobTitleAreaLabel: labels.jobTitleArea,
            taskAreaLabel: labels.taskArea
        };

        if (!validSamples?.length) {
            return empty;
        }

        const fieldLabels = this.getFieldLabels(labels);
        const processedSamples = validSamples.map(sample => this.processSample(sample));
        const groups = this.createSampleTableGroups(validSamples, processedSamples, options);

        const tableGroups: MultiSampleTableGroupView[] = groups.map((group, index) =>
            this.buildTableGroupView(group, fieldLabels, labels, index === 0));

        const taskAreaRows: MultiSampleTaskAreaRowView[] = processedSamples.map(sample => ({
            sampleNumber: sample.sampleNumber,
            taskDescription: sample.locationComments || labels.noTaskDescription
        }));

        return {
            tableGroups,
            taskAreaRows,
            hasData: true,
            concentrationsHeaderLabel: labels.concentrationsHeader,
            sampleNumberLabel: labels.sampleNumber,
            jobTitleAreaLabel: labels.jobTitleArea,
            taskAreaLabel: labels.taskArea
        };
    }

    /**
     * Flattens the selected samples into one row per (sample x ihLabResult) so a template can author
     * a per-result table with `[[#each computedData.detailedResultRows]]` — a shape the sample-as-column
     * matrix cannot express. Reuses the existing date / duration / TWA formatters; the exposure band and
     * SEG rating come from the caller-supplied resolvers.
     */
    buildDetailedResultRows(
        data: MultiSampleData | null | undefined,
        resolvers: DetailedResultRowResolvers
    ): MultiSampleDetailedResultRow[] {
        const rows: MultiSampleDetailedResultRow[] = [];

        for (const sample of (data?.samples ?? []).filter(s => s.sampleCategory !== SampleCategoryEnum.Blank)) {
            const date = this.formatDate(sample.startDate);
            const durationMinutes = this.getDurationMinutes(sample);
            const locationComments = sample.locationComments || '';
            const segName = sample.segName || '';

            for (const lr of sample.ihLabResults ?? []) {
                const units = lr.agentUnits;
                const isLoq = lr.sampleCensor === SampleCensorType.LessThan;
                const twa = lr.twa || 0;
                const effectiveOel = lr.correctedOEL || lr.oel || 0;
                const percentOfOel = !isLoq && effectiveOel > 0 ? (twa / effectiveOel) * 100 : null;
                const band = percentOfOel != null
                    ? resolvers.resolveExposureBand(percentOfOel, lr.jurisdictionId)
                    : EMPTY_EXPOSURE_BAND;
                const bandColor = sanitizeCssColor(band.color);
                const seg = resolvers.resolveSegRating(sample.segId, lr.agentId);

                rows.push({
                    date,
                    sampleNumber: sample.sampleNumber,
                    agentName: lr.agentName || 'Unknown',
                    jurisdictionName: lr.jurisdictionName || '',
                    durationMinutes,
                    result: this.formatTWAValue(twa, units, CENSOR_SYMBOLS[lr.sampleCensor] || '', isLoq),
                    exposureLimit: this.formatOelWithUnit(effectiveOel, units),
                    percentOfOel: percentOfOel == null ? '' : `${Math.round(percentOfOel)}%`,
                    percentOfOelValue: percentOfOel,
                    exposureBandName: band.name,
                    exposureBandColor: bandColor,
                    exposureBand: { ...band, color: bandColor },
                    locationComments,
                    segName,
                    segRating: seg.rating,
                    segRatingColor: sanitizeCssColor(seg.ratingColor),
                    methodOfAssessment: seg.methodOfAssessment
                });
            }
        }

        return rows;
    }

    private buildTableGroupView(
        group: InternalTableGroup,
        fieldLabels: { label: string; key: string }[],
        labels: MultiSampleTablePresentationLabels,
        isFirstGroup: boolean
    ): MultiSampleTableGroupView {
        const sampleColumns: MultiSampleSampleColumnView[] = group.samples.map(s => ({
            sampleNumber: s.sampleNumber,
            segName: s.segName,
            sampleType: this.formatFieldValue(s.sampleCategory),
            respType: this.formatFieldValue(s.respType),
            respApf: this.formatFieldValue(s.respAPF),
            mucExceeded: this.formatFieldValue(s.mucExceeded),
            startDate: this.formatFieldValue(s.startDate),
            startTime: this.formatFieldValue(s.startTime),
            duration: this.formatFieldValue(s.duration),
            volume: this.formatFieldValue(s.volume)
        }));

        const oelColumns: MultiSampleOelColumnView[] = group.oelColumns.map((col, i) => ({
            oelTypeName: col.oelTypeName,
            headerStyle: this.buildOelHeaderStyle(i)
        }));

        const fieldRows: MultiSampleFieldRowView[] = fieldLabels.map(field => ({
            label: field.label,
            values: group.samples.map(s => this.formatFieldValue(s[field.key]))
        }));

        const displayTables: MultiSampleDisplayTableView[] = group.jurisdictionTables.length > 0
            ? group.jurisdictionTables.map(j => ({
                showJurisdictionHeader: true,
                jurisdictionName: j.jurisdictionName,
                agentRows: j.agentRows.map(row => this.buildAgentRowView(row, group.oelColumns))
            }))
            : [{
                showJurisdictionHeader: false,
                jurisdictionName: '',
                agentRows: group.agentRows.map(row => this.buildAgentRowView(row, group.oelColumns))
            }];

        const legendItems: MultiSampleLegendItemView[] = group.oelColumns.map((col, i) => ({
            legendText: col.oelTypeName,
            swatchStyle: `background-color:${OEL_COLORS[i % OEL_COLORS.length]}`
        }));

        const oelRowspan = 1 + fieldLabels.length;
        const agentRows = this.buildAgentTemplateRows(displayTables);
        const jurisdictionHeader = displayTables.find(d => d.showJurisdictionHeader);
        const totalColspan = sampleColumns.length + 1 + group.oelColumnCount;

        return {
            sampleColumns,
            oelColumns,
            oelColumnCount: group.oelColumnCount,
            hasOelColumns: group.oelColumnCount > 0,
            jurisdictionColspan: sampleColumns.length + 1 + group.oelColumnCount,
            fieldRows,
            displayTables,
            legendItems,
            hasLegend: legendItems.length > 0,
            isFirstGroup,
            concentrationsHeaderLabel: labels.concentrationsHeader,
            sampleNumberLabel: labels.sampleNumber,
            jobTitleAreaLabel: labels.jobTitleArea,
            oelHeaderLabelLine1: labels.oelHeaderLine1,
            oelHeaderLabelLine2: labels.oelHeaderLine2,
            oelHeaderLabelLine3: labels.oelHeaderLine3,
            agentRows,
            showJurisdictionHeader: !!jurisdictionHeader,
            jurisdictionName: jurisdictionHeader?.jurisdictionName ?? '',
            totalColspan,
            oelRowspan
        };
    }

    private buildAgentTemplateRows(displayTables: MultiSampleDisplayTableView[]): MultiSampleAgentTemplateRowView[] {
        return displayTables.flatMap(dt => dt.agentRows).map(agent => ({
            agentName: agent.agentName,
            concentrationCells: agent.sampleCells.map(c => ({ concentration: c.display, cellStyle: c.cellStyle })),
            oelValueCells: agent.oelCells.map(c => ({ oelValue: c.display, cellStyle: c.cellStyle }))
        }));
    }

    private buildAgentRowView(row: InternalAgentRow, oelColumns: OelTypeData[]): MultiSampleAgentRowView {
        const oelCells: MultiSampleCellView[] = oelColumns.map((col, i) => ({
            display: row.oelValueByColumnKey[col.columnKey] ?? '-',
            cellStyle: this.buildOelCellStyle(i)
        }));

        return {
            agentName: row.agentName,
            sampleCells: row.sampleCells,
            oelCells
        };
    }

    private processSample(sample: Sample): ProcessedSample {
        return {
            sampleNumber: sample.sampleNumber,
            segName: sample.segName || 'N/A',
            sampleCategory: SampleCategoryEnum[sample.sampleCategory] || 'Unknown',
            startDate: this.formatDate(sample.startDate),
            startTime: this.formatTime(sample.startDate),
            duration: this.getDurationMinutes(sample),
            volume: Math.round((sample.correctedVolume || sample.sampleVolume || 0) * 1000) / 1000,
            respType: this.getRespType(sample.ppeAssignments),
            respAPF: this.getRespAPF(sample.ppeAssignments),
            mucExceeded: this.getMUCExceeded(sample),
            locationComments: sample.locationComments || '',
            rawSample: sample
        };
    }

    private createSampleTableGroups(
        samples: Sample[],
        processedSamples: ProcessedSample[],
        options: MultiSampleTablePresentationOptions
    ): InternalTableGroup[] {
        const groups: InternalTableGroup[] = [];

        for (let i = 0; i < processedSamples.length; i += SAMPLES_PER_TABLE) {
            const groupSamples = processedSamples.slice(i, i + SAMPLES_PER_TABLE);
            const groupRawSamples = samples.slice(i, i + SAMPLES_PER_TABLE);
            const jurisdictionMap = this.buildJurisdictionMap(groupRawSamples);
            const oelColumns = this.collectOelColumns(groupRawSamples, options);
            const oelColumnCount = oelColumns?.length || 0;

            if (jurisdictionMap.size > 0) {
                const allJurisdictionTables: InternalJurisdictionTable[] = Array.from(jurisdictionMap.entries()).map(([id, name]) => ({
                    jurisdictionId: id,
                    jurisdictionName: name,
                    agentRows: this.buildAgentRows(groupRawSamples, r => r.jurisdictionId === id, options)
                }));

                for (const chunk of this.splitJurisdictionsByAgents(allJurisdictionTables)) {
                    groups.push({ samples: groupSamples, agentRows: [], jurisdictionTables: chunk, oelColumns, oelColumnCount });
                }
            } else {
                const allAgentRows = this.buildAgentRows(groupRawSamples, () => true, options);
                const chunks = allAgentRows.length ? this.chunkWithEmptyFallback(allAgentRows, AGENTS_PER_TABLE) : [[]];
                for (const chunk of chunks) {
                    groups.push({ samples: groupSamples, agentRows: chunk, jurisdictionTables: [], oelColumns, oelColumnCount });
                }
            }
        }

        return groups;
    }

    private splitJurisdictionsByAgents(tables: InternalJurisdictionTable[]): InternalJurisdictionTable[][] {
        const total = tables.reduce((sum, t) => sum + t.agentRows.length, 0);
        if (total <= AGENTS_PER_TABLE) return [tables];

        const result: InternalJurisdictionTable[][] = [];
        let current: InternalJurisdictionTable[] = [], count = 0;

        for (const t of tables) {
            if (t.agentRows.length <= AGENTS_PER_TABLE) {
                if (count + t.agentRows.length > AGENTS_PER_TABLE && current.length) {
                    result.push(current);
                    current = [];
                    count = 0;
                }
                current.push(t);
                count += t.agentRows.length;
            } else {
                if (current.length) { result.push(current); current = []; count = 0; }
                for (const chunk of this.chunkWithEmptyFallback(t.agentRows, AGENTS_PER_TABLE)) {
                    result.push([{ jurisdictionId: t.jurisdictionId, jurisdictionName: t.jurisdictionName, agentRows: chunk }]);
                }
            }
        }
        if (current.length) result.push(current);
        return result;
    }

    private chunkWithEmptyFallback<T>(arr: T[], size: number): T[][] {
        const chunks = chunkArray(arr, size);
        return chunks.length ? chunks : [[]];
    }

    private collectOelColumns(samples: Sample[], options: MultiSampleTablePresentationOptions): OelTypeData[] {
        const oelMap = new Map<string, OelTypeData>();

        for (const sample of samples) {
            for (const r of sample.ihLabResults || []) {
                if (r.oel && r.oelTypeId && r.oelTypeName) {
                    const key = `oel_${r.oelTypeName.replace(/\s+/g, '_').toLowerCase()}`;
                    if (!oelMap.has(key) && !this.isExcluded(key, options, r.oelTypeId)) {
                        oelMap.set(key, { oelTypeId: r.oelTypeId, oelTypeName: r.oelTypeName, oelValue: r.oel, isAdjusted: false, columnKey: key });
                    }
                }

                if (!options.excludeAdjustedOel && (sample.applyShiftCorrection || r.isOelCorrectionApplied) && sample.shiftLength && r.correctedOEL) {
                    const hrs = Math.round(sample.shiftLength / 3600), key = `adjusted_${hrs}hr`;
                    if (!oelMap.has(key) && !this.isExcluded(key, options)) {
                        oelMap.set(key, { oelTypeId: r.oelTypeId, oelTypeName: `${hrs}-Hr Adjusted OEL`, oelValue: r.correctedOEL, isAdjusted: true, shiftLength: hrs, columnKey: key });
                    }
                }
            }
        }
        return this.sortOelTypes(Array.from(oelMap.values()));
    }

    private isExcluded(key: string, options: MultiSampleTablePresentationOptions, oelTypeId?: string): boolean {
        if (options.excludedOelColumnKeys?.includes(key)) return true;
        if (!options.selectedOelFilters?.length) return false;
        return key.startsWith('adjusted_') ? !options.selectedOelFilters.includes(key) : oelTypeId !== undefined && !options.selectedOelFilters.includes(oelTypeId);
    }

    private sortOelTypes(types: OelTypeData[]): OelTypeData[] {
        return types.sort((a, b) => {
            const ra = a.isAdjusted ? 1 : 0, rb = b.isAdjusted ? 1 : 0;
            return ra !== rb ? ra - rb : ra === 0 ? a.oelTypeName.localeCompare(b.oelTypeName) : (a.shiftLength || 0) - (b.shiftLength || 0);
        });
    }

    private buildJurisdictionMap(samples: Sample[]): Map<string, string> {
        const map = new Map<string, string>();
        samples.forEach(s => s.ihLabResults?.forEach(r => { if (r.jurisdictionId && r.jurisdictionName) map.set(r.jurisdictionId, r.jurisdictionName); }));
        return map;
    }

    private buildAgentRows(
        samples: Sample[],
        filter: (r: IhLabResult) => boolean,
        options: MultiSampleTablePresentationOptions
    ): InternalAgentRow[] {
        const agentMap = new Map<string, { name: string; units: Units; symbol: string }>();
        samples.forEach(s => s.ihLabResults?.filter(filter).forEach(r => {
            if (!agentMap.has(r.agentId)) agentMap.set(r.agentId, { name: r.agentName || 'Unknown', units: r.agentUnits, symbol: UnitSymbols[r.agentUnits] || '' });
        }));

        return Array.from(agentMap.entries())
            .map(([agentId, info]) => {
                const oelTypes = this.getOelTypesForAgent(agentId, samples, filter, options);
                const oelValueByColumnKey: Record<string, string> = {};
                oelTypes.forEach(o => { oelValueByColumnKey[o.columnKey] = this.formatOelValue(o.oelValue, info.units); });
                const sampleCells: MultiSampleCellView[] = samples.map(s => {
                    const lr = s.ihLabResults?.find(r => r.agentId === agentId && filter(r));
                    if (!lr) return { display: '-', cellStyle: '' };
                    const isLoq = lr.sampleCensor === SampleCensorType.LessThan;
                    const twa = lr.twa || 0;
                    const exceeded = isLoq ? null : this.findExceededOel(twa, lr, oelTypes);
                    const display = this.formatTWAValue(twa, info.units, CENSOR_SYMBOLS[lr.sampleCensor] || '', isLoq);
                    return { display, cellStyle: this.buildExceedanceStyle(isLoq, !!exceeded, exceeded?.color) };
                });
                return { agentId, agentName: info.name, agentUnits: info.units, oelTypes, oelValueByColumnKey, sampleCells };
            })
            .sort((a, b) => a.agentName.localeCompare(b.agentName));
    }

    private findExceededOel(twa: number, lr: IhLabResult, oelTypes: OelTypeData[]): { color: string } | null {
        if (!twa || twa <= 0) return null;

        const exceeded = oelTypes.filter(o =>
            o.isAdjusted
                ? (lr.isOelCorrectionApplied && typeof lr.correctedOEL === 'number' && lr.correctedOEL > 0 && twa > lr.correctedOEL)
                : (lr.oelTypeId === o.oelTypeId && typeof lr.oel === 'number' && lr.oel > 0 && twa > lr.oel)
        ).map(o => ({ oelValue: o.isAdjusted ? lr.correctedOEL! : lr.oel!, columnKey: o.columnKey }));

        if (!exceeded.length) return null;
        const highest = exceeded.reduce((max, c) => c.oelValue > max.oelValue ? c : max);
        return { color: OEL_COLORS[oelTypes.findIndex(o => o.columnKey === highest.columnKey) % OEL_COLORS.length] || FALLBACK_OEL_COLOR };
    }

    private getOelTypesForAgent(
        agentId: string,
        samples: Sample[],
        filter: (r: IhLabResult) => boolean,
        options: MultiSampleTablePresentationOptions
    ): OelTypeData[] {
        const oelMap = new Map<string, OelTypeData>(), shifts = new Set<number>();

        for (const sample of samples) {
            const results = sample.ihLabResults?.filter(r => r.agentId === agentId && filter(r)) || [];
            for (const r of results) {
                if (r.oel && r.oelTypeId && r.oelTypeName) {
                    const key = `oel_${r.oelTypeName.replace(/\s+/g, '_').toLowerCase()}`;
                    if (!oelMap.has(key) && !this.isExcluded(key, options, r.oelTypeId)) {
                        oelMap.set(key, { oelTypeId: r.oelTypeId, oelTypeName: r.oelTypeName, oelValue: r.oel, isAdjusted: false, columnKey: key });
                    }
                }
            }
            if (!options.excludeAdjustedOel && (sample.applyShiftCorrection || results.some(r => r.isOelCorrectionApplied)) && sample.shiftLength) {
                shifts.add(Math.round(sample.shiftLength / 3600));
            }
        }

        if (!options.excludeAdjustedOel) {
            for (const hrs of shifts) {
                const key = `adjusted_${hrs}hr`;
                if (this.isExcluded(key, options)) continue;
                for (const sample of samples) {
                    if (sample.shiftLength && Math.round(sample.shiftLength / 3600) === hrs) {
                        const r = sample.ihLabResults?.find(x => x.agentId === agentId && filter(x) && x.correctedOEL && x.isOelCorrectionApplied);
                        if (r && !oelMap.has(key)) {
                            oelMap.set(key, { oelTypeId: r.oelTypeId, oelTypeName: `${hrs}-Hr Adjusted OEL`, oelValue: r.correctedOEL!, isAdjusted: true, shiftLength: hrs, columnKey: key });
                        }
                    }
                }
            }
        }
        return this.sortOelTypes(Array.from(oelMap.values()));
    }

    private formatTWAValue(value: number, units: Units, censor: string, isLoq: boolean): string {
        if (isLoq) return '< LOQ';
        if (value == null) return '-';
        const val = `${this.valueConversionService.getParameterValueAsPerAgentUnit(value, units).toFixed(4)} ${this.valueConversionService.getParameterUnitSymbol(units)}`;
        return censor ? `${censor} ${val}` : val;
    }

    private formatOelValue(value: number, units: Units): string {
        return value == null ? '-' : this.valueConversionService.getParameterValueAsPerAgentUnit(value, units).toFixed(4);
    }

    private formatOelWithUnit(value: number, units: Units): string {
        if (!value) return '-';
        return `${this.valueConversionService.getParameterValueAsPerAgentUnit(value, units).toFixed(4)} ${this.valueConversionService.getParameterUnitSymbol(units)}`;
    }

    private getDurationMinutes(sample: Sample): number {
        const seconds = sample.runTime || sample.sampleDuration;
        return seconds ? Math.round(seconds / 60) : 0;
    }

    private buildExceedanceStyle(isLoq: boolean, exceedsOel: boolean, exceededOelColor?: string): string {
        if (isLoq) return LOQ_CELL_STYLE;
        return exceedsOel && exceededOelColor ? `background-color:${exceededOelColor} !important;font-weight:bold` : '';
    }

    private buildOelHeaderStyle(columnIndex: number): string {
        return `background-color:${OEL_COLORS[columnIndex % OEL_COLORS.length]} !important;color:#000000;font-weight:bold`;
    }

    private buildOelCellStyle(columnIndex: number): string {
        return this.buildOelHeaderStyle(columnIndex);
    }

    private formatFieldValue(value: unknown): string {
        return value == null ? '' : String(value);
    }

    private formatTime(d: string | Date | number): string {
        return this.formatDateTime(d, this.dateFormatService.userTimeFormat);
    }

    private formatDate(d: string | Date | number): string {
        return this.formatDateTime(d, this.dateFormatService.userDateFormat);
    }

    private formatDateTime(d: string | Date | number, format: string): string {
        if (!d) return '';
        if (typeof d === 'string') {
            return this.dateFormatService.convertUTCDateStringToUserTimeZoneString(d, format);
        }
        if (typeof d === 'number') {
            return this.dateFormatService.formatUtcTimestampToUserTimezone(d, format);
        }
        return this.dateFormatService.formatDateTimeWithTimeZoneToString(d, format);
    }

    private getRespType(ppes: PpeItem[]): string | null {
        const ppe = this.findRespPpe(ppes);
        return ppe ? ((ppe as unknown as PpeBase).ppeName || (ppe as unknown as PpeBase).name || null) : null;
    }

    private getRespAPF(ppes: PpeItem[]): number {
        const ppe = this.findRespPpe(ppes);
        if (!ppe) return 1;

        const base = ppe as unknown as PpeBase;
        if (base.rating && base.rating > 0) return base.rating;
        if (base.apf) { const n = parseInt(base.apf, 10); if (!isNaN(n)) return n; }

        const name = (base.ppeName || base.name)?.toLowerCase();
        if (!name) return 1;
        const match = name.match(/apf\s*[=:]?\s*(\d+)/i);
        if (match) return parseInt(match[1], 10);
        if (name.includes('n95') || (name.includes('half') && name.includes('face'))) return 10;
        if (name.includes('full') && name.includes('face')) return 50;
        if (name.includes('papr')) return 25;
        if (name.includes('scba')) return 10000;
        return 1;
    }

    private findRespPpe(ppes?: PpeItem[]): PpeItem | null {
        return ppes?.find(p => { const b = p as unknown as PpeBase; return b.ppeType === 0 || b.iconUrl?.includes('mask.svg'); }) || null;
    }

    private getMUCExceeded(sample: Sample): string | null {
        const apf = this.getRespAPF(sample.ppeAssignments);
        if (!apf) return null;
        const exceeded = sample.ihLabResults?.some(lr => {
            if (!lr.oel || !lr.twa) return false;
            return lr.twa > (lr.isOelCorrectionApplied && lr.correctedOEL ? lr.correctedOEL : lr.oel) * apf;
        });
        return exceeded ? 'Yes' : 'No';
    }

    private getFieldLabels(labels: MultiSampleTablePresentationLabels): { label: string; key: string }[] {
        return [
            { label: labels.sampleType + ':', key: 'sampleCategory' },
            { label: labels.respType + ':', key: 'respType' },
            { label: labels.respApf + ':', key: 'respAPF' },
            { label: labels.mucExceeded + ':', key: 'mucExceeded' },
            { label: labels.startDate + ':', key: 'startDate' },
            { label: labels.startTime + ':', key: 'startTime' },
            { label: labels.durationMin + ':', key: 'duration' },
            { label: labels.volumeLiters + ':', key: 'volume' }
        ];
    }
}
